import { ENTITY_LABELS } from "../../../utils/labelMapping";
import {
  Skeleton,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import { useContext, useEffect, useRef, useState } from "react";

import { InfoOutlined } from "@mui/icons-material";
import ErrorView from "../../../Component/ErrorView/ErrorView";
import MUIIconButton from "../../../Component/UI/MUIIconButton/MUIIconButton";
import MUIToolTip from "../../../Component/UI/ActionTooltip/MUIToolTip";
import { extractDomain } from "../../../utils/helpers";
import { ReportScreenShot } from "../../Dashboard/common/MetricDetails";
import DetailedTableModal from "../BrandAuditMetricDetails"
import { DiscoveryContext } from "../discovery-context";
import MUICard from "./../../../Component/UI/MUICard/MUICard";
import Table, {
  StyledTableCell,
  StyledTableRow,
} from "./../../../Component/UI/Table/Table";
import classes from "./CoreAttributes.module.css";
import {
  createActionMetric,
  fetchActionMetrics,
} from "../../../redux/actions/metricActions";
import { toast } from "react-toastify";
import { AuthContext } from "../../../context/auth-context";
import { useDispatch } from "react-redux";
import { formatSourceData,formatOldSourceData } from "../../../utils/metric-sources"
import BrandAvatar from "../../../Component/Avatar/Avatar"

const TABLE_HEADERS = [
  {
    id: "attr",
    name: `${ENTITY_LABELS.Brand} Pillars`,
  },
];

export default function CoreAttributes({ category, entity }) {
  const discoveryCtx = useContext(DiscoveryContext);
  const abortControllerRef = useRef(null);
  const [showModal, setShowModal] = useState(false);
    const searchParams = new URLSearchParams(window.location.search);
  const isReporting = searchParams.get("isReporting") === "true";

  const [isActionLoading, setIsActionLoading] = useState(false);
  const { user } = useContext(AuthContext);
  const dispatch = useDispatch();
  const customerId = user?.customer_id;
  const llm = discoveryCtx.state.selectedLLM;
  const primaryMarket = discoveryCtx.markets[0];
  const selectedPersona = discoveryCtx.state.selectedPersona;
  const type = discoveryCtx.type;
  const headers = [discoveryCtx.entity, ...(discoveryCtx?.competitors?.[category]?.map((item) => item.name) ?? [])]
  const showOldSourcesRecord = discoveryCtx.state.sourceValidationStatus === "PENDING";
  function formatName(str) {
    return str
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  }

  const saveActions = async (actionStatement, explanation, sources, brand) => {
    setIsActionLoading(true);
    const modifiedObj = {
      title: actionStatement,
      rationale: explanation,
      brand,
      persona: "General Persona",
      geography: primaryMarket == "General Market" ? "General Location" : primaryMarket,
      market: "General Market",
      category,
      llm,
      isProduct: type === "product" ? true : false,
      sources,
    };

    const payload = {
      action_json: [modifiedObj],
      is_archived: 0,
      is_active: 1,
    };
    dispatch(createActionMetric(customerId, payload))
      .then(() => {
        dispatch(fetchActionMetrics(customerId));
        setIsActionLoading(false);
        toast.success("Action Added!", {
          autoClose: 700,
        });
      })
      .catch(() => {
        setIsActionLoading(false);
        toast.error("Something went wrong!", {
          autoClose: 700,
        });
      });
  }

  function renderRows(rowItem, index) {
    const attName = formatName(rowItem?.name);
    const searchParams = new URLSearchParams(window.location.search);
  const isReporting = searchParams.get("isReporting") === "true";
    return (
      <StyledTableRow key={index}>
        <StyledTableCell className={classes.attribute_headings}>
          {formatName(rowItem.name)}
        </StyledTableCell>

        {headers?.map((brand, idx) => {
          const attributeScore = rowItem?.scores?.find(
            (item) => item.name?.toLowerCase() === brand?.toLowerCase()
          );

          if (!attributeScore) {
            // no matching score for this brand → show "-"
            return (
              <StyledTableCell
                key={idx}
                style={{ color: "#000" }}
                className={classes.attribute_score}
              >
                -
              </StyledTableCell>
            );
          }

          // we found a matching score → render that cell
          let color = "#dc3545";
          let rating = "low";

          if (!attributeScore.score) {
            color = "#000";
          } else if (attributeScore.score >= 9) {
            rating = "high";
            color = "#198754";
          } else if (attributeScore.score >= 7) {
            rating = "fair";
            color = "#2196F3";
          }
          const attributeSources = Array.isArray(attributeScore?.sources) && attributeScore?.sources?.length > 0 ? attributeScore?.sources : attributeScore?.productSources

          const entityName = discoveryCtx.entity?.toLowerCase();
          const attributeName = attributeScore?.name?.toLowerCase();
          const llmName = llm?.toLowerCase();

          const isCustomTooltip =
            attributeName === entityName &&
            llmName?.includes("search") &&
            (showOldSourcesRecord || isValidated);

          const sources = isCustomTooltip
            ? showOldSourcesRecord
              ? formatOldSourceData(attributeSources, selectedLLM)
              : formatSourceData(attributeSources, selectedLLM)
            : {};

          const TooltipComponent = isCustomTooltip ? MUIToolTip : Tooltip;

          const tooltipProps = isCustomTooltip
            ? {
              metric: `${ENTITY_LABELS.Brand} Pillars`,
              attribute: attName,
              metricScore: Number(attributeScore.score).toFixed(0),
              selectedInput: {
                llm,
                market:primaryMarket,
                category,
                brand: attributeScore.name,
                product: attributeScore.name
              },
              addToActionHandler: saveActions,
              actionText: "Add To Action",
              isActionLoading: isActionLoading,
              title: attributeScore.explanation,
              arrow: true,
              isBrandAudit: true,
              sources,
              slotProps: {
                popper: {
                  modifiers: [
                    {
                      name: "offset",
                      options: {
                        offset: [0, -18],
                      },
                    },
                  ],
                },
              }

            }
            : {
              title: attributeScore.explanation,
              arrow: true,
              slotProps: {
                popper: {
                  modifiers: [
                    {
                      name: "offset",
                      options: {
                        offset: [0, -18],
                      },
                    },
                  ],
                },
              },
            };

          return attributeScore.score  &&  !isReporting? (
            <TooltipComponent {...tooltipProps}>
              <StyledTableCell
                style={{ color }}
                className={classes.attribute_score}
              >
                {Number(attributeScore.score).toFixed(0)}
              </StyledTableCell>
            </TooltipComponent>
          ) : (
            <StyledTableCell
              key={idx}
              style={{ color }}
              className={classes.attribute_score}
            >
              {attributeScore?.score? Number(attributeScore.score).toFixed(0):"-"}
            </StyledTableCell>
          );
        })}
      </StyledTableRow>
    );
  }

  function LoadingSkeletion() {
    return (
      <div className="p-3">
        <Skeleton variant="rectangular" height={30} />
        {Array(8)
          .fill(0)
          .map((_, index) => (
            <Skeleton
              variant="rectangular"
              width={index % 2 === 0 ? "80%" : "97%"}
              key={index}
              className="my-3"
              height={20}
            />
          ))}
      </div>
    );
  }


  function renderHeaders(showModalPop = showModal) {
    const headers = [
      ...TABLE_HEADERS,
      ...discoveryCtx.competitors['default'].map((item) => ({
        id: item.name,
        name: item.name,
      })),
      ...discoveryCtx.competitors[category].map((item) => ({
        id: item.name,
        name: item.name,
      })),
    ];
    const entityAndCompetitorData = [...discoveryCtx.competitors['default'], ...discoveryCtx.competitors[category]]
    return (
      <TableHead className={classes.table_head}>
        <TableRow>
          {headers.map((header, index) => {
            let ctxCompetitor = null;
            if (index !== 0) {
              if (index === 1) {
                ctxCompetitor = discoveryCtx.competitors['default'][0];
              } else {
                ctxCompetitor = discoveryCtx.competitors[category][index - 2];
              }
            }

            let domain = "";
            if (ctxCompetitor && ctxCompetitor?.domain) {
              domain = extractDomain(ctxCompetitor?.domain);
            }

            return (
              <TableCell key={index} className={index == 0 ? classes.table_head_cell_index : classes.table_head_cell}>
                <Tooltip title={!isReporting?header.name:""} placement="top">
                  <div className={classes.table_head_content}>
                    {index != 0 && (
                      // <img
                      //   data-html2canvas-ignore
                      //   className={classes.table_head_logo}
                      //   src={`https://img.logo.dev/${domain}?token=pk_SaDf4t2eRsW5Fc84zgJRZA`}
                      // />
                                            <BrandAvatar
                                            domainName={domain}
                                            brandName={header.name}
                                            />
                    )}
                    <div
                      className={
                        index === 0
                          ? classes.table_head_text_comp
                          : classes.table_head_text
                      }
                    >
                      {/* {header.name} */}
                      <div className={classes.table_head_title}>
                        <span>
                          {header.name}
                        </span>
                        {index === 0 && !showModalPop && (
                          <MUIIconButton onClick={(showModal) => setShowModal(true)} id="brand_pillars">
                            <InfoOutlined />
                          </MUIIconButton>
                        )}
                      </div>
                    </div>
                  </div>
                </Tooltip>
              </TableCell>
            );
          })}
        </TableRow>
      </TableHead>
    );
  }

  let coreAttributesData = [];
  let loading = true;
  let hasError = false;
  const transactionStatus = discoveryCtx.state.transactionStatus
  const selectedLLM = discoveryCtx.state.selectedLLM;
  let coreAttributes = discoveryCtx?.core_attributes?.[selectedLLM]?.[category]?.[primaryMarket]?.[selectedPersona];
  let isValidated = coreAttributes?.isValidated || false;
  let competitors = discoveryCtx?.competitors?.[category]?.map((item) => item.name) ?? [];
  let brand = discoveryCtx?.competitors?.['default']?.[0].name;
  competitors.unshift(brand);

  if (
    coreAttributes &&
    Object.keys(coreAttributes).length > 0 &&
    coreAttributes?.summary
  ) {
    coreAttributesData = Object.keys(
      coreAttributes.category_averages
    ).map((item) => {
      const scores = coreAttributes.products.map((p) => ({
        name: p.brandName || p.name,
        score: p.attributes[item]?.score,
        explanation: p.attributes[item]?.explanation,
        sources: p.attributes[item]?.sources,
        productSources: p.sources
      }));

      return {
        name: item,
        scores: scores,
      };
    });
    loading = false;
  } else if (transactionStatus === "RETRY") {
    hasError = true;
    loading = false;
  } else if (transactionStatus === "RUNNING") {
    hasError = false;
    loading = true;
  } else {
    loading = false;
  }
  return (
    <>
      <MUICard variant="elevated" elevation={0} className={classes.card}>
        {coreAttributesData &&
          coreAttributesData.length > 0 &&
          !loading &&
          !hasError && (
            <>
              <Table
                elevation={0}
                data={coreAttributesData}
                renderHeaders={() => renderHeaders()}
                render={renderRows}
              />
              <DetailedTableModal
                show={showModal}
                onClose={() => setShowModal(false)}
                data={coreAttributesData}
                headers={[discoveryCtx.entity, ...(discoveryCtx?.competitors?.[category]?.map((item) => item.name) ?? [])]}
                renderHeaders={renderHeaders}
                formatName={formatName}
                classes={classes}
                pdfHeight={400}
                pdfTitle={`${entity}-Brand Pillars`}
              />
              <ReportScreenShot
                data={coreAttributesData}
                headers={[discoveryCtx.entity, ...(discoveryCtx?.competitors?.[category]?.map((item) => item.name) ?? [])]}
                renderHeaders={renderHeaders}
                formatName={formatName}
                classes={classes}
              />
            </>

          )}
        {loading && !hasError && <LoadingSkeletion />}
        {hasError && (
          <div className={classes.error}>
            <ErrorView
              title={"Error Fetching Brand Pillars"}
              message={"Request Failed"}
            />
          </div>
        )}
      </MUICard>


    </>
  );
}

